// <tri-expand-all> — the Expand all / Collapse all control over the scan
// summary's <details> groups. Markup convention:
//
//   <tri-expand-all>
//     <button data-expand-trigger aria-expanded="false">
//       <span data-expand-label>Expand all</span>
//     </button>
//     …
//     <details data-expand-target>…</details>  ×N
//   </tri-expand-all>
//
// Trigger rule: if ANY target is closed → open all; otherwise close all.
// The label tracks reality rather than a toggle flag — per-group manual
// toggles are observed too (capture phase, because `toggle` doesn't
// bubble), so after a user hand-opens every group the button correctly
// reads "Collapse all". Without JS the groups still open individually;
// the button is pure enhancement.

class TriExpandAll extends HTMLElement {
	#lifetime: AbortController | null = null;

	connectedCallback() {
		const trigger = this.querySelector<HTMLElement>("[data-expand-trigger]");
		if (!trigger) return;

		this.#lifetime?.abort();
		this.#lifetime = new AbortController();
		const { signal } = this.#lifetime;

		trigger.addEventListener(
			"click",
			() => {
				const targets = this.#targets();
				const anyClosed = targets.some((details) => !details.open);
				for (const details of targets) details.open = anyClosed;
				this.#sync();
			},
			{ signal },
		);

		// `toggle` fires on the <details> and does not bubble — capture
		// still sees it on the way down, keeping the label honest after
		// manual per-group toggles.
		this.addEventListener("toggle", () => this.#sync(), {
			capture: true,
			signal,
		});

		this.#sync();
	}

	disconnectedCallback() {
		this.#lifetime?.abort();
	}

	#targets(): HTMLDetailsElement[] {
		return [
			...this.querySelectorAll<HTMLDetailsElement>(
				"details[data-expand-target]",
			),
		];
	}

	#sync() {
		const trigger = this.querySelector<HTMLElement>("[data-expand-trigger]");
		const label = this.querySelector<HTMLElement>("[data-expand-label]");
		if (!trigger || !label) return;

		const targets = this.#targets();
		const allOpen =
			targets.length > 0 && targets.every((details) => details.open);
		label.textContent = allOpen ? "Collapse all" : "Expand all";
		trigger.setAttribute("aria-expanded", String(allOpen));
	}
}

if (!customElements.get("tri-expand-all")) {
	customElements.define("tri-expand-all", TriExpandAll);
}

export {};
